import {
  reifyElementSubtree,
  reifyJSONNodesToComponentMap,
  reifyPageNodesToComponentMap,
  reifyComponentJSONNodesOnComponentMap,
  dataTypesFromComponents,
  dataTypesFromSymbolNodes,
  migrateFlattenOverrides,
} from '@packages/systems/core/render';
import {getChildren} from '@packages/utilities/platform';
import {Plugins, Component} from '@packages/systems/wfdl';
import {type ExpressionElement} from '@packages/systems/wfdl-ast';
import type {JSONNode} from '@packages/shared/types/JSONNode';
import {
  compressJSONNodes,
  exportElementAsJSONNodeArray,
  legacy_exportElementAsJSONNodeArray,
  exportSymbolsAsJSONNodes,
  legacy_exportElementAsJSONNode,
  exportElementsAsJSONNodesForPage,
  exportSymbolAsJSONNodes,
  exportSymbolsAsJSONNodesById,
} from './ExportElementUtils';

export {compressJSONNodes, exportElementAsJSONNodeArray};
import {Qualified} from '@packages/systems/wfdl-Qualified';

const removeOrphanedSymbols = (
  pageNodes: Array<JSONNode>,
  symbolNodes: Array<JSONNode>,
  plugins: Plugins.PluginsType
) => {
  const validComponentInstanceIndex = Plugins.getPluginComponents(
    plugins
  ).reduce(
    (map, component, [_, name]: [any, any]) => {
      map.set(Component.nameToString(name), true);
      return map;
    },
    symbolNodes.reduce((map, node) => {
      if (node.data && node.data.sym && node.data.sym.root) {
        map.set(node._id, true);
      }
      return map;
    }, new Map())
  );

  return pageNodes.filter((node) => {
    const inst = node.data && node.data.sym && node.data.sym.inst;
    return !inst || validComponentInstanceIndex.get(inst);
  });
};

/**
 * Drop page nodes whose symbol instance names a component that does not exist,
 * given the set of component names an instance may legally reference.
 *
 * The set-based sibling of {@link removeOrphanedSymbols}, for callers that
 * already hold a hydrated component map and therefore have no symbol nodes to
 * derive the set from — see {@link validComponentInstancesFor} and
 * {@link Tree.reifyPageNodesAgainstComponents}.
 */
const removeOrphanedSymbolsByInstances = (
  pageNodes: Array<JSONNode>,
  validComponentInstances: ReadonlySet<string>
) =>
  pageNodes.filter((node) => {
    const inst = node.data && node.data.sym && node.data.sym.inst;
    return !inst || validComponentInstances.has(inst);
  });

/**
 * The component names a symbol instance may reference, derived from a hydrated
 * site's component map instead of from the raw symbol nodes. Produces the same
 * set {@link removeOrphanedSymbols} builds from symbol nodes: reifying a full
 * payload files every symbol root under `__SitePlugin` keyed by
 * `Component.name(node._id)`, which is exactly the key `sym.inst` carries. The
 * page component is not an instance target, so it is excluded.
 */
const validComponentInstancesFor = (
  components: Component.ComponentMap,
  plugins: Plugins.PluginsType
): ReadonlySet<string> => {
  const valid = new Set<string>();
  Plugins.getPluginComponents(plugins).reduce(
    (acc, _component, [_, name]: [any, any]) => {
      acc.add(Component.nameToString(name));
      return acc;
    },
    valid
  );
  components.reduceAtPluginName(
    Plugins.name('__SitePlugin'),
    (acc, _component, name) => {
      if (name !== 'page') acc.add(name);
      return acc;
    },
    valid
  );
  return valid;
};

/**
 * # Tree
 *
 * We have 2 primary ways of representing a tree of Webflow Elements.
 *
 * - JSON Nodes - How Element's are persisted in the Database & Transported.
 * - Expressions - The structure used by the Designer, Editor & Renderer.
 *
 * The tree abstraction encapsulates converting between these formats.
 */
export class Tree {
  _componentMap: Component.ComponentMap;

  constructor(componentMap: Component.ComponentMap) {
    this._componentMap = componentMap;
  }

  static create(componentMap: Component.ComponentMap) {
    return new Tree(componentMap);
  }

  /**
   * The primary path for reifying from the database. Since DOM Nodes
   * and symbol nodes are provided together. We remove orphaned symbol instances.
   */
  static fromJSONNodes(
    pageNodes: Array<JSONNode>,
    symbolNodes: Array<JSONNode>,
    plugins: Plugins.PluginsType
  ) {
    const dataTypes = dataTypesFromSymbolNodes(plugins)(symbolNodes);
    const updatedPageNodes = migrateFlattenOverrides(
      plugins,
      dataTypes,
      pageNodes
    );
    const updatedSymbolNodes = migrateFlattenOverrides(
      plugins,
      dataTypes,
      symbolNodes
    );

    return new Tree(
      reifyJSONNodesToComponentMap(
        removeOrphanedSymbols(updatedPageNodes, updatedSymbolNodes, plugins),
        updatedSymbolNodes,
        plugins,
        dataTypes
      )
    );
  }

  /**
   * In cases where we'd like a tree that contains plugin components,
   * for example, during rendering on the server.
   */
  static fromJSONNodesWithPluginComponents(
    pageNodes: Array<JSONNode>,
    symbolNodes: Array<JSONNode>,
    plugins: Plugins.PluginsType
  ) {
    return new Tree(
      Plugins.getPluginComponents(plugins).union(
        Tree.fromJSONNodes(pageNodes, symbolNodes, plugins).asComponentMap()
      )
    );
  }

  /**
   * Reify ONE page's DOM against an already-hydrated component map and return
   * the map carrying that page — the equivalent of
   * `fromJSONNodesWithPluginComponents` for a single page, without reifying
   * every site component again.
   *
   * Used by hosts that hydrate a site's globals once and then project several
   * pages from the same site data: the components, their data types and the
   * migration result are identical for every page, so only the page root is
   * page-specific. The orphaned-instance filter matches `fromJSONNodes`, so a
   * page seeded this way projects the same DOM as a full hydration.
   */
  static reifyPageNodesAgainstComponents(
    pageNodes: Array<JSONNode>,
    components: Component.ComponentMap,
    plugins: Plugins.PluginsType
  ): Component.ComponentMap {
    // One migration, inside `reifyPageNodesToComponentMap` — the same path the
    // Designer's page switch takes. The orphan filter only reads `sym.inst`,
    // which the migration does not change.
    const pageComponentMap = reifyPageNodesToComponentMap(
      removeOrphanedSymbolsByInstances(
        pageNodes,
        validComponentInstancesFor(components, plugins)
      ),
      components,
      plugins
    );
    return components.union(pageComponentMap);
  }

  /**
   * When a user changes the current page in the Designer, we want to
   * keep the existing components and only reify the new page with the
   * existing components.
   */
  updatePageWithJSONNodes(
    pageNodes: Array<JSONNode>,
    plugins: Plugins.PluginsType
  ) {
    this._componentMap = this._componentMap.union(
      reifyPageNodesToComponentMap(pageNodes, this._componentMap, plugins)
    );

    return this;
  }

  /**
   * When a user changes the current page in the Designer for the non-primary
   * locale, we want to reify the new page with the existing components, but
   * also take into account localized symbols.
   */
  updatePageWithJSONNodesAndSymbols(
    pageNodes: Array<JSONNode>,
    symbolNodes: Array<JSONNode>,
    plugins: Plugins.PluginsType
  ) {
    const dataTypes = dataTypesFromSymbolNodes(plugins)(symbolNodes);
    this._componentMap = this._componentMap.union(
      reifyJSONNodesToComponentMap(
        migrateFlattenOverrides(plugins, dataTypes, pageNodes),
        symbolNodes,
        plugins,
        dataTypes
      )
    );

    return this;
  }

  updateWithComponentJSONNodes(
    componentNodes: Array<JSONNode>,
    componentType: Qualified<Component.ComponentName>,
    plugins: Plugins.PluginsType
  ) {
    this._componentMap = reifyComponentJSONNodesOnComponentMap(
      this._componentMap,
      componentType,
      componentNodes,
      plugins
    );

    return this;
  }

  getElementWithJSONNodes(
    jsonNodes: Array<JSONNode>,
    plugins: Plugins.PluginsType
  ): ExpressionElement {
    return reifyElementSubtree(jsonNodes, this._componentMap, plugins);
  }

  /**
   * Return the tree as a ComponentMap.
   */
  asComponentMap() {
    return this._componentMap;
  }

  getElementAsJSONNodes(element: ExpressionElement): Array<JSONNode> {
    return exportElementAsJSONNodeArray(this._componentMap)(element);
  }

  /**
   * Returns the JSONNodes that constitute the given element.
   * We run the compression algorithm so that default fields are removed
   * from the returned nodes.
   */
  getElementAsJSONNodesCompressed(
    element: ExpressionElement,
    plugins: Plugins.PluginsType
  ): Array<JSONNode> {
    const jsonNodes = this.getElementAsJSONNodes(element);
    // The compression algorithm mutates the incoming arguments
    compressJSONNodes(plugins)(jsonNodes);
    return jsonNodes;
  }

  /**
   * A temporary implementation of legacy serialization logic for backwards
   * copmatibiliity.
   *
   * @deprecated use `Tree.getElementAsJSONNodesCompressed` as this does not account for numerous Component or Element capabilities
   */
  __DEPRECATED__getElementWithoutChildrenAsJSONNodes(
    element: ExpressionElement
  ): JSONNode {
    // TEMP: we are temporarily using legacy serialization logic here. Single-node sync
    // expects the User Component instance JSON nodes to have the override JSON nodes
    // in the `node.data.sym.overrides` so it cannot use the new serialization that
    // flattens the overrides into arrays of node IDs yet.
    // TODO Structure - Implement JsonDomUtils that can replace a flattened User Component
    //   override subtree, and make the single-node sync backend using it. That will allow
    //   us to get rid of the legacy serialization utils.
    return legacy_exportElementAsJSONNode(this._componentMap)(element);
  }

  /**
   * A temporary implementation of legacy serialization logic for backwards
   * copmatibiliity.
   *
   * @deprecated use `Tree.getElementAsJSONNodesCompressed` as this does not account for numerous Component or Element capabilities
   */
  __DEPRECATED__getElementContentNodes = (
    element: ExpressionElement
  ): {
    jsonContentNodes: Array<JSONNode>;
    rootChildIds: Array<string>;
  } => {
    // TEMP: we are temporarily using legacy serialization logic here. Single-node sync
    // expects the User Component instance JSON nodes to have the override JSON nodes
    // in the `node.data.sym.overrides` so it cannot use the new serialization that
    // flattens the overrides into arrays of node IDs yet.
    // TODO Structure - Implement JsonDomUtils that can replace a flattened User Component
    //   override subtree, and make the single-node sync backend using it. That will allow
    //   us to get rid of the legacy serialization utils.
    const jsonNodes = legacy_exportElementAsJSONNodeArray(this._componentMap)(
      element
    );
    const jsonContentNodes = jsonNodes.slice(1);
    const children = getChildren(element);
    let rootChildIds: Array<string> = [];

    if (children && children.length) {
      rootChildIds = children.map((n) => n.id);
    }

    return {
      jsonContentNodes,
      rootChildIds,
    };
  };

  getComponentAsJSONNodes(
    componentName: Qualified<Component.ComponentName>,
    plugins: Plugins.PluginsType
  ): JSONNode[] {
    const component = this._componentMap.get(componentName);
    if (!component) return [];

    const dataTypes = dataTypesFromComponents(plugins, this._componentMap);

    // Even though `exportSymbolsAsJSONNodes` flattens the overrides, and
    // de-duplicates JSON node IDs, we still need to call `migrateFlattenOverrides`
    // as it patches dataTypes.
    const updatedNodes = migrateFlattenOverrides(
      plugins,
      dataTypes,
      exportSymbolAsJSONNodes(this._componentMap)(component)
    );

    // The compression algorithm mutates the incoming arguments
    const compress = compressJSONNodes(plugins);
    compress(updatedNodes);

    return updatedNodes;
  }

  // Serializes only page-level nodes. Useful when symbol nodes are not needed
  toPageJSONNodes(plugins: Plugins.PluginsType): Array<JSONNode> {
    const dataTypes = dataTypesFromComponents(plugins, this._componentMap);
    return this.serializePageNodes(plugins, dataTypes);
  }

  // Serializes only component nodes. Useful when page nodes are not needed
  toSymbolJSONNodes(plugins: Plugins.PluginsType): Array<JSONNode> {
    const dataTypes = dataTypesFromComponents(plugins, this._componentMap);
    return this.serializeSymbolNodes(plugins, dataTypes);
  }

  // Serializes both page and symbol nodes
  toJSONNodes(plugins: Plugins.PluginsType): {
    symbolNodes: Array<JSONNode>;
    pageNodes: Array<JSONNode>;
  } {
    const dataTypes = dataTypesFromComponents(plugins, this._componentMap);
    const updatedPageNodes = this.serializePageNodes(plugins, dataTypes);
    const updatedSymbolNodes = this.serializeSymbolNodes(plugins, dataTypes);

    return {
      pageNodes: updatedPageNodes,
      symbolNodes: updatedSymbolNodes,
    };
  }

  /**
   * Returns an array of uncompressed JSONNodes that are mapped by their component ID.
   * Compression is not applied to the returned nodes because the map is only used for
   * read-only purposes of processing symbol nodes to components.
   */
  toJSONNodesByComponentId(
    plugins: Plugins.PluginsType
  ): Record<string, Array<JSONNode>> {
    return exportSymbolsAsJSONNodesById(plugins)(this._componentMap);
  }

  private serializePageNodes(
    plugins: Plugins.PluginsType,
    dataTypes: Plugins.DataTypes
  ): Array<JSONNode> {
    // Even though `exportElementsAsJSONNodesForPage` flattens the overrides, and
    // de-duplicates JSON node IDs, we still need to call `migrateFlattenOverrides`
    // as it patches dataTypes.
    const updatedPageNodes = migrateFlattenOverrides(
      plugins,
      dataTypes,
      exportElementsAsJSONNodesForPage(this._componentMap)
    );
    // The compression algorithm mutates the incoming arguments
    compressJSONNodes(plugins)(updatedPageNodes);
    return updatedPageNodes;
  }

  private serializeSymbolNodes(
    plugins: Plugins.PluginsType,
    dataTypes: Plugins.DataTypes
  ): Array<JSONNode> {
    // Even though `exportSymbolsAsJSONNodes` flattens the overrides, and
    // de-duplicates JSON node IDs, we still need to call `migrateFlattenOverrides`
    // as it patches dataTypes.
    const updatedSymbolNodes = migrateFlattenOverrides(
      plugins,
      dataTypes,
      exportSymbolsAsJSONNodes(plugins)(this._componentMap)
    );
    // The compression algorithm mutates the incoming arguments
    compressJSONNodes(plugins)(updatedSymbolNodes);
    return updatedSymbolNodes;
  }
}
